import * as qs from 'qs';
import {
  DisconnectorModel,
  CircuitBreakerModel,
  TransformerModel,
  TemperatureVariableModel,
  ChangeGeneralDataResponse,
  AssetTypes,
  AssetModulesLookup,
  AssetModulesLookupRequest,
  LastReadingResponse,
} from 'sigma-mappings';
import Http from '@/services/http';
import translate from '@/services/translate';
import generalDataFieldsDefinitions
  from '@/domain/data/installation/assetInformation/generalDataFieldsDefinitions';
import { assetsTypesIds } from '@/domain/data/installation/assets';

const urls = {
  assetModuleLookup: (id, query) => `ativo/${id}/modulos/lookups?${query}`,
  disconnectorData: id => `ativo/${id}/dados-gerais/seccionadora`,
  circuitBreakerData: id => `ativo/${id}/dados-gerais/disjuntor`,
  transformerData: id => `ativo/${id}/dados-gerais/transformador`,
  temperatureVariables: id => `/instalacao/${id}/variaveis-temperatura`,
  lastReading: id => `/campoModuloAtivo/${id}/ultima-leitura`,
};

/* istanbul ignore next */
const error = {
  errorHandling: {
    responseMessageParams: {
       $MA029: (data) => {
        if (!data && !data.parameters) return null;
        const returnedField = generalDataFieldsDefinitions[data.parameters[2]];
        return {
          FaixaInicial: data.parameters[0],
          FaixaFinal: data.parameters[1],
          NomeDoCampo: translate(returnedField.label),
        };
      },
      $MA039: (data) => {
        const splitField = (data.field || {}).split('.');
        const field = splitField[1] ? splitField[1] : splitField;
        const returnedField = generalDataFieldsDefinitions[field];
        if (!returnedField) return null;

        return {
          field: translate(returnedField.label),
        };
      },
      $MA038: (data) => {
        const parameters = data.parameters || {};
        const firstField = generalDataFieldsDefinitions[parameters[0]];
        const secondField = generalDataFieldsDefinitions[parameters[1]];
        if (!firstField || !secondField) return null;

        return {
          NomeDoCampo: translate(firstField.label),
          FaixaInicial: translate(secondField.label),
        };
      },
      $ME025: (data) => {
        const field1 = generalDataFieldsDefinitions[data.parameters[0]];
        const field2 = generalDataFieldsDefinitions[data.parameters[1]];
        if (!field1 || !field2) return null;

        return {
          field1: translate(field1.label),
          field2: translate(field2.label),
        };
      },
      $MA051: (data) => {
         const returnedField = generalDataFieldsDefinitions[data.parameters[1]];
         return {
           maxLength: data.parameters[0],
           fieldName: translate(returnedField.label),
         };
      },
    },
  },
};

export default {
  getGeneralDisconnectorData(assetId) {
    /**
    *  Obtém os dados da aba Dados Gerais da Seccionadora
    *  @param {guid} assetId ID do ativo que será atualizado
    */
    const http = new Http();
    return http
      .get(urls.disconnectorData(assetId))
      .then(({ data }) => new DisconnectorModel(data));
  },

  /**
  *  Atualiza os dados Gerais da Seccionadora
  *  @param {guid} assetId ID do ativo que será atualizado
  *  @param {date} payload.type
  *  @param {enum} payload.maneuverType
  *  @param {number} payload.nominalTension
  *  @param {number} payload.nominalChain
  *  @param {number} payload.nominalFrequency
  *  @param {string} payload.supportableShortChainDuration
  *  @param {number} payload.appliedTension1Min
  *  @param {number} payload.atmosphericImpulse
  *  @param {enum} payload.installationPlace
  *  @param {string} payload.maintenanceHistory
  *  @param {string} payload.observation
  */
  updateGeneralDisconnectorData(payload, assetId) {
    const mappedPayload = new DisconnectorModel.MapBack(payload);
    const http = new Http(error);
    return http
      .put(urls.disconnectorData(assetId), mappedPayload)
      .then(({ data }) => data);
  },

  getGeneralCircuitBreakerData(assetId) {
    /**
    *  Obtém os dados da aba Dados Gerais do Disjuntor
    *  @param {guid} assetId ID do ativo que será atualizado
    */
    const http = new Http();
    return http
      .get(urls.circuitBreakerData(assetId))
      .then(({ data }) => new CircuitBreakerModel(data));
  },

  /**
  *  Atualiza os dados Gerais do Disjuntor
  *  @param {guid} assetId ID do ativo que será atualizado
  *  @param {number} payload.nominalTension
  *  @param {number} payload.nominalChain
  *  @param {number} payload.nominalFrequency
  *  @param {string} payload.supportableShortChainDuration
  *  @param {enum} payload.installationPlace
  *  @param {string} payload.maintenanceHistory
  *  @param {string} payload.observation
  *  @param {enum} payload.type
  *  @param {enum} payload.installationPlace
  *  @param {enum} payload.electricArchExtinctionMethod
  *  @param {number} payload.typicalOpeningTime
  *  @param {number} payload.maximumClosingTime
  *  @param {string} payload.nominalOperationSequence
  *  @param {enum} payload.mechanismOperation
  *  @param {object} payload.interruptionCapacity
  *  @param {object} payload.supportableNominalTension
  */
  updateGeneraCircuitBreakerData(payload, assetId) {
    const mappedPayload = new CircuitBreakerModel.MapBack(payload);
    const http = new Http(error);
    return http
      .put(urls.circuitBreakerData(assetId), mappedPayload)
      .then(({ data }) => data);
  },

  getGeneralTransformerData(assetId) {
    /**
    *  Obtém os dados da aba Dados Gerais do Transformador
    *  @param {guid} assetId ID do ativo que será atualizado
    */
    const http = new Http();
    return http
      .get(urls.transformerData(assetId))
      .then(({ data }) => new TransformerModel(data));
  },

  /**
  *  Atualiza os dados Gerais do Transformador
  *  @param {guid} assetId ID do ativo que será atualizado
  *  @param {number} payload.nominalTension
  *  @param {number} payload.nominalChain
  *  @param {number} payload.nominalFrequency
  *  @param {string} payload.supportableShortChainDuration
  *  @param {enum} payload.installationPlace
  *  @param {string} payload.maintenanceHistory
  *  @param {string} payload.observation
  *  @param {enum} payload.type
  *  @param {enum} payload.installationPlace
  *  @param {enum} payload.electricArchExtinctionMethod
  *  @param {number} payload.typicalOpeningTime
  *  @param {number} payload.maximumClosingTime
  *  @param {string} payload.nominalOperationSequence
  *  @param {enum} payload.mechanismOperation
  *  @param {object} payload.interruptionCapacity
  *  @param {object} payload.supportableNominalTension
  */
  updateGeneralTransformerData(payload, assetId) {
    const mappedPayload = new TransformerModel.MapBack(payload);
    const http = new Http(error);
    return http
      .put(urls.transformerData(assetId), mappedPayload)
      .then(({ data }) => new ChangeGeneralDataResponse(data));
  },

  getTemperatureVariables(installationId) {
    const http = new Http();
    return http
      .get(urls.temperatureVariables(installationId))
      .then(({ data }) => data.map(item => new TemperatureVariableModel(item)));
  },

  getAssetModuleLookup(assetId, moduleType) {
    const http = new Http(error);

    const queryParams = new AssetModulesLookupRequest({ moduleType });
    return http
      .get(urls.assetModuleLookup(assetId, qs.stringify(queryParams)))
      .then(({ data }) => data.map(item => new AssetModulesLookup(item)));
  },

  getAssetInfosByAssetType(assetTypeId, assetId) {
    const assetCalls = {
      [assetsTypesIds[AssetTypes.TRANSFORMER]]:
        this.getGeneralTransformerData,
      [assetsTypesIds[AssetTypes.DISCONNECTOR]]:
        this.getGeneralDisconnectorData,
      [assetsTypesIds[AssetTypes.CIRCUIT_BREAKER]]:
        this.getGeneralCircuitBreakerData,
    };
    return assetCalls[assetTypeId](assetId)
      .then(response => response);
  },

  getLastReading(contactId) {
    const http = new Http();
    return http
      .get(urls.lastReading(contactId))
      .then(({ data }) => new LastReadingResponse(data));
  },
};
